<template>
    <Sidebar
      @open-create-modal="openCreateModal"
      @logout="logout"
    />

    <!-- Main Content -->
    <div class="lg:ml-64 min-h-screen bg-gray-50">
      <div class="p-4 sm:p-6 lg:p-8 max-w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Admin Control Panel</h2>
        
        <!-- Tabs Navigation -->
        <div class="flex border-b mb-4 overflow-x-auto">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="px-4 py-2 text-gray-600 hover:text-blue-600 whitespace-nowrap"
            :class="{ 'border-b-2 border-blue-600 text-blue-600': activeTab === tab.id }"
          >
            {{ tab.label }}
          </button>
        </div>
        
        <!-- Tab Content -->
        <div v-if="activeTab === 'onboard'" class="p-4 bg-white rounded-lg shadow-sm">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Onboard User</h3>
          <div class="space-y-4">
            <input
              v-model="newUser.email"
              type="email"
              placeholder="Email"
              class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              v-model="newUser.name"
              type="text"
              placeholder="Name"
              class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select v-model="newUser.role" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">Select Role</option>
              <option v-for="role in roles" :key="role" :value="role">{{ formatRoleName(role) }}</option>
            </select>
            <select v-model="newUser.manager" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">No Manager</option>
              <option v-for="user in users" :key="user._id" :value="user._id">{{ user.name }}</option>
            </select>
            <select v-model="newUser.department" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select Department</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
            </select>
            <input
              v-model="newUser.password"
              type="password"
              placeholder="Password"
              class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              @click="onboardUser"
              class="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!isFormValid"
            >
              Onboard User
            </button>
            <p v-if="onboardMessage" class="text-sm mt-2" :class="onboardMessage.type === 'success' ? 'text-green-600' : 'text-red-600'">
              {{ onboardMessage.text }}
            </p>
          </div>
        </div>
  
        <div v-if="activeTab === 'manage'" class="p-4 bg-white rounded-lg shadow-sm">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Manage Users</h3>
          <div class="mb-4 flex flex-col sm:flex-row gap-4">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or email"
              class="flex-1 p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <select v-model="roleFilter" class="p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option value="">All Roles</option>
              <option v-for="role in roles" :key="role" :value="role">{{ formatRoleName(role) }}</option>
            </select>
          </div>
          
          <!-- Desktop Table View (hidden on mobile) -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full border-collapse bg-white">
              <thead>
                <tr class="bg-gray-100">
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Name</th>
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Email</th>
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Role</th>
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Manager</th>
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Department</th>
                  <th class="border p-3 text-left text-gray-700 whitespace-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="user in filteredUsers" :key="user._id" class="hover:bg-gray-50">
                  <td class="border p-3">{{ user.name }}</td>
                  <td class="border p-3">{{ user.email }}</td>
                  <td class="border p-3">{{ formatRoleName(user.role) }}</td>
                  <td class="border p-3">{{ user.manager?.name || 'None' }}</td>
                  <td class="border p-3">{{ user.department || 'N/A' }}</td>
                  <td class="border p-3">
                    <div class="flex gap-2">
                      <button @click="editUser(user)" class="text-blue-500 hover:underline">Edit</button>
                      <button @click="deleteUser(user._id)" class="text-red-500 hover:underline">Delete</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile Card View (visible only on mobile) -->
          <div class="md:hidden space-y-4">
            <div v-for="user in filteredUsers" :key="user._id" class="bg-white border rounded-lg p-4 shadow-sm">
              <div class="space-y-3">
                <div class="flex justify-between items-start">
                  <div>
                    <h4 class="font-semibold text-gray-900">{{ user.name }}</h4>
                    <p class="text-sm text-gray-600">{{ user.email }}</p>
                  </div>
                  <span class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                    {{ formatRoleName(user.role) }}
                  </span>
                </div>

                <div class="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p class="text-gray-500">Manager</p>
                    <p class="font-medium text-gray-900">{{ user.manager?.name || 'None' }}</p>
                  </div>
                  <div>
                    <p class="text-gray-500">Department</p>
                    <p class="font-medium text-gray-900">{{ user.department || 'N/A' }}</p>
                  </div>
                </div>

                <div class="flex gap-2 pt-2 border-t">
                  <button
                    @click="editUser(user)"
                    class="flex-1 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    Edit
                  </button>
                  <button
                    @click="deleteUser(user._id)"
                    class="flex-1 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div v-if="manageMessage" class="mt-4 text-sm" :class="manageMessage.type === 'success' ? 'text-green-600' : 'text-red-600'">
            {{ manageMessage.text }}
          </div>
          
          <!-- Edit User Modal -->
          <div v-if="showEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div class="bg-white rounded-lg p-4 sm:p-6 w-full max-w-md my-8">
              <h3 class="text-lg font-semibold text-gray-700 mb-4">Edit User</h3>
              <div class="space-y-4">
                <input
                  v-model="editUserData.email"
                  type="email"
                  placeholder="Email"
                  class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <input
                  v-model="editUserData.name"
                  type="text"
                  placeholder="Name"
                  class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select v-model="editUserData.role" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">Select Role</option>
                  <option v-for="role in roles" :key="role" :value="role">{{ formatRoleName(role) }}</option>
                </select>
                <select v-model="editUserData.manager" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  <option value="">No Manager</option>
                  <option v-for="user in users" :key="user._id" :value="user._id">{{ user.name }}</option>
                </select>
                <select v-model="editUserData.department" class="w-full p-3 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                <option value="">Select Department</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
                </select>
                <div class="flex gap-2">
                  <button
                    @click="updateUser"
                    class="flex-1 bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-300"
                  >
                    Update
                  </button>
                  <button
                    @click="showEditModal = false"
                    class="flex-1 bg-gray-300 text-gray-700 p-3 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
  
        <div v-if="activeTab === 'approval'" class="p-4 bg-white rounded-lg shadow-sm">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Approval Order</h3>
          <p class="text-sm text-gray-500 mb-4">Use the buttons to reorder approval roles</p>
          
          <div class="max-w-md">
            <div class="space-y-2">
              <div 
                v-for="(role, index) in approvalOrder" 
                :key="role"
                class="p-3 bg-gray-50 border rounded-md flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center">
                  <svg class="w-5 h-5 mr-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
                  </svg>
                  <span class="font-medium">{{ index + 1 }}.</span>
                  <span class="ml-2">{{ formatRoleName(role) }}</span>
                </div>
                <div class="flex gap-1">
                  <button 
                    @click="moveUp(index)"
                    :disabled="index === 0"
                    class="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"/>
                    </svg>
                  </button>
                  <button 
                    @click="moveDown(index)"
                    :disabled="index === approvalOrder.length - 1"
                    class="p-1 text-gray-500 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <button
              @click="saveApprovalOrder"
              class="w-full bg-purple-500 text-white p-3 rounded-lg mt-4 hover:bg-purple-600 focus:ring-2 focus:ring-purple-300"
            >
              Save Approval Order
            </button>
            
            <p v-if="approvalMessage" class="mt-4 text-sm" :class="approvalMessage.type === 'success' ? 'text-green-600' : 'text-red-600'">
              {{ approvalMessage.text }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import axios from 'axios';
  import Sidebar from './Sidebar.vue';

  export default {
    components: { Sidebar },
    data() {
      return {
        activeTab: 'onboard',
        user: JSON.parse(localStorage.getItem('user') || '{}'),
        tabs: [
          { id: 'onboard', label: 'Onboard User' },
          { id: 'manage', label: 'Manage Users' },
          { id: 'approval', label: 'Approval Order' },
        ],
        newUser: {
          email: '',
          name: '',
          role: '',
          manager: '',
          password: '',
          department: ''
        },
        editUserData: {
          _id: '',
          email: '',
          name: '',
          role: '',
          manager: '',
          department: ''
        },
        users: [],
        searchQuery: '',
        roleFilter: '',
        showEditModal: false,
        approvalOrder: ['direct_manager', 'hod', 'managing_director'],
        roles: ['employee', 'direct_manager', 'hod', 'managing_director', 'finance', 'ceo', 'admin'],
        approvalRoles: ['direct_manager', 'hod', 'managing_director'],
        departments: ['Sales', 'Technical', 'Transport', 'Finance & Logistics', 'Management', 'Software Development', 'IT Support' ],
        onboardMessage: null,
        manageMessage: null,
        approvalMessage: null,
      };
    },
    computed: {
      isFormValid() {
        return this.newUser.email && this.newUser.name && this.newUser.role && this.newUser.password && this.newUser.department;
      },
      filteredUsers() {
        return this.users.filter(user => {
          // Safely handle undefined values
          const matchesSearch = (user.name && user.name.toLowerCase().includes(this.searchQuery.toLowerCase())) ||
                                (user.email && user.email.toLowerCase().includes(this.searchQuery.toLowerCase()));
          const matchesRole = !this.roleFilter || user.role === this.roleFilter;
          return matchesSearch && matchesRole;
        });
      },
    },
    async mounted() {
      await this.loadUsers();
      await this.loadApprovalOrder();
    },
    methods: {
      async loadUsers() {
        try {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/users', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.users = res.data;
        } catch (error) {
          this.manageMessage = { 
            type: 'error', 
            text: 'Failed to load users: ' + (error.response?.data?.message || error.message) 
          };
        }
      },
      async loadApprovalOrder() {
        try {
          const res = await axios.get('https://guarantee.iwacufinance.com/cash-request/api/requests/approval-order', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.approvalOrder = res.data;
        } catch (error) {
          this.approvalMessage = { 
            type: 'error', 
            text: 'Failed to load approval order: ' + (error.response?.data?.message || error.message) 
          };
        }
      },
      async onboardUser() {
        try {
          if (!this.isFormValid) {
            this.onboardMessage = { type: 'error', text: 'Please fill in all required fields' };
            return;
          }
          const userData = { ...this.newUser };
          if (!userData.manager) {
            userData.manager = null;
          }
          await axios.post('https://guarantee.iwacufinance.com/cash-request/api/users', userData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.resetForm();
          await this.loadUsers();
          this.onboardMessage = { type: 'success', text: 'User onboarded successfully!' };
        } catch (error) {
          this.onboardMessage = { 
            type: 'error', 
            text: 'Failed to onboard user: ' + (error.response?.data?.message || error.message) 
          };
        }
      },
      resetForm() {
        this.newUser = { email: '', name: '', role: '', manager: '', password: '' };
      },
      editUser(user) {
        this.editUserData = { ...user };
        this.showEditModal = true;
      },
      async updateUser() {
            try {
                // Ensure we have the user ID
                if (!this.editUserData.id && !this.editUserData._id) {
                this.manageMessage = {
                    type: 'error',
                    text: 'User ID is missing. Please try again.'
                };
                return;
                }

                // Prepare the data to send (include ID in the body)
                const updateData = {
                id: this.editUserData.id || this.editUserData._id,
                email: this.editUserData.email,
                name: this.editUserData.name,
                role: this.editUserData.role,
                department: this.editUserData.department,
                manager: this.editUserData.manager || null,
                ...(this.editUserData.password && this.editUserData.password.trim() !== '' && {
                    password: this.editUserData.password
                })
                };

                await axios.put(`https://guarantee.iwacufinance.com/cash-request/api/users`, updateData, {
                headers: { 
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                });

                this.showEditModal = false;
                await this.loadUsers();
                this.manageMessage = { type: 'success', text: 'User updated successfully!' };

                // Clear the edit form
                this.editUserData = {};

            } catch (error) {
                console.error('Update user error:', error);
                this.manageMessage = {
                type: 'error',
                text: 'Failed to update user: ' + (error.response?.data?.message || error.message)
                };
            }
            },
      async deleteUser(id) {
        if (!confirm('Are you sure you want to delete this user?')) return;
        try {
          await axios.delete(`https://guarantee.iwacufinance.com/cash-request/api/users/${id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          await this.loadUsers();
          this.manageMessage = { type: 'success', text: 'User deleted successfully!' };
        } catch (error) {
          this.manageMessage = { 
            type: 'error', 
            text: 'Failed to delete user: ' + (error.response?.data?.message || error.message) 
          };
        }
      },
      moveUp(index) {
        if (index > 0) {
          const newOrder = [...this.approvalOrder];
          [newOrder[index], newOrder[index - 1]] = [newOrder[index - 1], newOrder[index]];
          this.approvalOrder = newOrder;
        }
      },
      moveDown(index) {
        if (index < this.approvalOrder.length - 1) {
          const newOrder = [...this.approvalOrder];
          [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
          this.approvalOrder = newOrder;
        }
      },
      async saveApprovalOrder() {
        try {
          if (this.approvalOrder.length === 0) {
            this.approvalMessage = { type: 'error', text: 'Please select at least one approval role' };
            return;
          }
          await axios.post('https://guarantee.iwacufinance.com/cash-request/api/requests/approval-order', { order: this.approvalOrder }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          this.approvalMessage = { type: 'success', text: 'Approval order saved successfully!' };
          this.$emit('order-updated');
        } catch (error) {
          this.approvalMessage = { 
            type: 'error', 
            text: 'Failed to save approval order: ' + (error.response?.data?.message || error.message) 
          };
        }
      },
      formatRoleName(role) {
        return role
          ? role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          : 'N/A';
      },
      openCreateModal() {
        // Handle opening create modal
        console.log('Opening create modal');
      },
      logout() {
        // Handle logout
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        this.$router.push('/');
      }
    },
  };
  </script>
  
<style scoped>
  /* Custom styles for better drag indication */
  .drag-handle:hover {
    color: #3b82f6;
  }
  
  /* Ensure proper scrolling */
  .overflow-x-auto {
    scrollbar-width: thin;
    scrollbar-color: #cbd5e0 #f7fafc;
  }
  
  .overflow-x-auto::-webkit-scrollbar {
    height: 6px;
  }
  
  .overflow-x-auto::-webkit-scrollbar-track {
    background: #f7fafc;
  }
  
  .overflow-x-auto::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
  }
  
  .overflow-x-auto::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
</style>
